import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface TriviaQuestion {
  prompt: string;
  choices: [string, string, string, string];
  correctIndex: 0 | 1 | 2 | 3;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: string;
}

interface TriviaWrongEvent {
  viewer: string;
  letter: string;
  ts: number;
}

interface TriviaState {
  pool?: TriviaQuestion[];
  index: number;
  currentQuestion: TriviaQuestion | null;
  questionExpiresAt: number | null;
  answeredBy: string | null;
  eliminated: string[];
  scores: Record<string, number>;
  streaks?: Record<string, number>;
  lastWrong?: TriviaWrongEvent | null;
  lastTimeoutAt?: number | null;
  finished: boolean;
}

interface TriviaConfig {
  questionDurationSec: number;
  maxQuestions: number;
}

const DIFFICULTY_COLOR: Record<'easy' | 'medium' | 'hard', string> = {
  easy: '#22c55e',
  medium: '#f59e0b',
  hard: '#ef4444',
};
const CHOICE_LETTERS = ['A', 'B', 'C', 'D'] as const;

export function TriviaBody({ state, config }: { state: TriviaState; config: TriviaConfig | null }) {
  const top = topScores(state.scores, 5);
  const prevQuestionRef = useRef<string>('');
  const prevAnsweredByRef = useRef<string | null>(null);
  const prevWrongTsRef = useRef<number>(0);
  const prevTimeoutAtRef = useRef<number>(0);
  const prevFinishedRef = useRef<boolean>(false);
  const [wrongStamp, setWrongStamp] = useState<TriviaWrongEvent | null>(null);
  const [showTimeUp, setShowTimeUp] = useState(false);

  // New question → stinger.
  useEffect(() => {
    const qid = state.currentQuestion?.prompt ?? '';
    if (qid && qid !== prevQuestionRef.current) {
      prevQuestionRef.current = qid;
      void gameSounds.play('phase_change');
    }
  }, [state.currentQuestion?.prompt]);

  // Correct answer fires → ding.
  useEffect(() => {
    if (state.answeredBy && state.answeredBy !== prevAnsweredByRef.current) {
      void gameSounds.play('correct');
      void gameSounds.play('reveal');
    }
    prevAnsweredByRef.current = state.answeredBy;
  }, [state.answeredBy]);

  // New wrong pick → red X stamp.
  useEffect(() => {
    const lw = state.lastWrong;
    if (!lw) return;
    if (lw.ts <= prevWrongTsRef.current) return;
    prevWrongTsRef.current = lw.ts;
    setWrongStamp(lw);
    void gameSounds.play('incorrect');
    const t = setTimeout(() => setWrongStamp(null), 1400);
    return () => clearTimeout(t);
  }, [state.lastWrong]);

  // Question timer expired with no answer → TIME'S UP stamp.
  useEffect(() => {
    const t = state.lastTimeoutAt ?? 0;
    if (t && t > prevTimeoutAtRef.current) {
      prevTimeoutAtRef.current = t;
      setShowTimeUp(true);
      void gameSounds.play('loser');
      const timer = setTimeout(() => setShowTimeUp(false), 1600);
      return () => clearTimeout(timer);
    }
  }, [state.lastTimeoutAt]);

  // Round end fanfare.
  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      void gameSounds.play('winner');
      void gameSounds.play('celebration');
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished]);

  if (state.finished) {
    return <FinishedView top={top} />;
  }
  if (!state.currentQuestion) {
    return (
      <div className="tv-stage">
        <div className="tv-loading">
          <div className="tv-loading-eyebrow">Loading question batch…</div>
          <div className="tv-loading-spinner" />
        </div>
        <TriviaStyles />
      </div>
    );
  }

  const total = state.pool?.length ?? config?.maxQuestions ?? 10;
  const current = (state.index ?? 0) + 1;

  return (
    <div className="tv-stage">
      <svg className="tv-watermark" viewBox="0 0 200 200" aria-hidden>
        <text
          x="50%"
          y="58%"
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="220"
          fontFamily="Orbitron, sans-serif"
          fontWeight="900"
          fill="currentColor"
        >
          ?
        </text>
      </svg>

      <div className="tv-content">
        <QuestionCard
          question={state.currentQuestion}
          revealed={!!state.answeredBy}
          eliminated={state.eliminated.length}
          countdownExpiresAt={state.questionExpiresAt}
          countdownTotal={config?.questionDurationSec ?? 30}
          current={current}
          total={total}
        />

        <div className="tv-choices">
          {state.currentQuestion.choices.map((choice, i) => (
            <Choice
              key={i}
              letter={CHOICE_LETTERS[i]!}
              text={choice}
              isCorrect={i === state.currentQuestion!.correctIndex}
              revealed={!!state.answeredBy}
            />
          ))}
        </div>

        {state.answeredBy && <Reveal name={state.answeredBy} />}
      </div>

      {wrongStamp && <WrongStamp event={wrongStamp} />}
      {showTimeUp && <TimeUpStamp />}

      <TriviaStyles />
    </div>
  );
}

function QuestionCard({
  question,
  revealed,
  eliminated,
  countdownExpiresAt,
  countdownTotal,
  current,
  total,
}: {
  question: TriviaQuestion;
  revealed: boolean;
  eliminated: number;
  countdownExpiresAt: number | null;
  countdownTotal: number;
  current: number;
  total: number;
}) {
  return (
    <div className="tv-q-card">
      <div className="tv-q-chips">
        <span className="tv-q-chip tv-q-chip-progress">
          {current} / {total}
        </span>
        {question.category && <span className="tv-q-chip tv-q-chip-cat">{question.category}</span>}
        <span
          className="tv-q-chip tv-q-chip-diff"
          style={{
            color: DIFFICULTY_COLOR[question.difficulty],
            borderColor: `${DIFFICULTY_COLOR[question.difficulty]}80`,
            background: `${DIFFICULTY_COLOR[question.difficulty]}1f`,
          }}
        >
          {question.difficulty}
        </span>
        {eliminated > 0 && !revealed && (
          <span className="tv-q-chip tv-q-chip-elim">{eliminated} out</span>
        )}
      </div>
      <div className="tv-q-prompt">{question.prompt}</div>
      {!revealed && <Countdown expiresAt={countdownExpiresAt} total={countdownTotal} />}
    </div>
  );
}

function Choice({
  letter,
  text,
  isCorrect,
  revealed,
}: {
  letter: string;
  text: string;
  isCorrect: boolean;
  revealed: boolean;
}) {
  const cls = revealed
    ? isCorrect
      ? 'tv-choice tv-choice-correct'
      : 'tv-choice tv-choice-dim'
    : 'tv-choice';
  return (
    <div className={cls}>
      {revealed && isCorrect && <div className="tv-choice-sweep" />}
      <div className="tv-choice-letter">
        <span>{letter}</span>
      </div>
      <div className="tv-choice-text">{text}</div>
      <svg className="tv-choice-burst" viewBox="0 0 20 20" aria-hidden>
        <path
          d="M10 0 L11.5 8.5 L20 10 L11.5 11.5 L10 20 L8.5 11.5 L0 10 L8.5 8.5 Z"
          fill="currentColor"
          opacity="0.5"
        />
      </svg>
    </div>
  );
}

function Countdown({ expiresAt, total }: { expiresAt: number | null; total: number }) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, ((expiresAt ?? 0) - Date.now()) / 1000),
  );
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setRemaining(Math.max(0, (expiresAt - Date.now()) / 1000));
    tick();
    const t = setInterval(tick, 100);
    return () => clearInterval(t);
  }, [expiresAt]);
  const pct = Math.max(0, Math.min(1, remaining / total));
  const warn = remaining <= 5;
  return (
    <div className="tv-bar-row">
      <div className="tv-bar-track">
        <div className={`tv-bar-fill ${warn ? 'warn' : ''}`} style={{ width: `${pct * 100}%` }} />
      </div>
      <div className={`tv-bar-num ${warn ? 'warn' : ''}`}>{Math.ceil(remaining)}s</div>
    </div>
  );
}

function Reveal({ name }: { name: string }) {
  return (
    <div className="tv-reveal">
      <div className="tv-reveal-badge">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0b0918"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>
      <div className="tv-reveal-body">
        <div className="tv-reveal-eyebrow">First correct</div>
        <div className="tv-reveal-name">{name}</div>
      </div>
    </div>
  );
}

function WrongStamp({ event }: { event: TriviaWrongEvent }) {
  return (
    <div className="tv-wrong-stamp" key={event.ts}>
      <div className="tv-wrong-x">✕</div>
      <div className="tv-wrong-label">
        <div className="tv-wrong-name">{event.viewer}</div>
        <div className="tv-wrong-sub">picked {event.letter} — OUT</div>
      </div>
    </div>
  );
}

function TimeUpStamp() {
  return (
    <div className="tv-timeup-stamp">
      <div className="tv-timeup-text">TIME&apos;S UP</div>
    </div>
  );
}

function FinishedView({ top }: { top: Array<[string, number]> }) {
  return (
    <div className="tv-stage tv-stage-end">
      <div className="tv-end-card">
        <div className="tv-end-eyebrow">Trivia complete</div>
        <div className="tv-end-title">{top[0] ? `${top[0][0]} wins` : 'No one scored'}</div>
        {top.length > 0 && (
          <div className="tv-end-board">
            {top.map(([name, score], i) => (
              <div key={name} className={`tv-end-row ${i === 0 ? 'top' : ''}`}>
                <span className="tv-end-rank">#{i + 1}</span>
                <span className="tv-end-name">{name}</span>
                <span className="tv-end-score">{score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <TriviaStyles />
    </div>
  );
}

function topScores(scores: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}

function TriviaStyles() {
  return (
    <style>{`
      .tv-stage {
        flex: 1;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 40px 80px;
        background:
          radial-gradient(60% 60% at 50% 35%, rgba(245, 158, 11, 0.10) 0%, transparent 60%),
          radial-gradient(80% 80% at 50% 100%, rgba(245, 158, 11, 0.05) 0%, transparent 60%);
        font-family: 'Rajdhani', sans-serif;
        overflow: hidden;
      }
      .tv-stage-end { padding: 40px; }
      .tv-watermark {
        position: absolute;
        inset: 0;
        margin: auto;
        width: 720px;
        height: 720px;
        color: rgba(245, 158, 11, 0.06);
        pointer-events: none;
        animation: tv-watermark-float 12s ease-in-out infinite;
      }
      @keyframes tv-watermark-float {
        0%, 100% { transform: translate(0, 0) rotate(-3deg); }
        50% { transform: translate(0, -16px) rotate(3deg); }
      }
      .tv-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        color: var(--gx-text-muted);
        font-weight: 600;
        letter-spacing: 2px;
      }
      .tv-loading-eyebrow {
        font-family: 'Orbitron', sans-serif;
        font-size: 22px;
        letter-spacing: 4px;
        color: #f59e0b;
        text-shadow: 0 0 20px rgba(245, 158, 11, 0.5);
      }
      .tv-loading-spinner {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        border: 4px solid rgba(245, 158, 11, 0.18);
        border-top-color: #f59e0b;
        animation: tv-spin 0.9s linear infinite;
      }
      @keyframes tv-spin { to { transform: rotate(360deg); } }

      .tv-content {
        position: relative;
        z-index: 1;
        width: min(1280px, 100%);
        display: flex;
        flex-direction: column;
        gap: 28px;
      }

      .tv-q-card {
        position: relative;
        padding: 32px 40px;
        border-radius: 24px;
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.06) 0%, rgba(255, 255, 255, 0.015) 100%);
        border: 1px solid rgba(245, 158, 11, 0.35);
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.04) inset,
          0 0 80px -20px rgba(245, 158, 11, 0.45),
          0 24px 60px -24px rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(20px);
        -webkit-backdrop-filter: blur(20px);
        animation: gx-slide-in-down 0.45s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .tv-q-chips {
        display: flex;
        gap: 8px;
        align-items: center;
        margin-bottom: 14px;
      }
      .tv-q-chip {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 2px;
        text-transform: uppercase;
        padding: 5px 12px;
        border-radius: 999px;
        border: 1px solid;
      }
      .tv-q-chip-progress {
        color: white;
        background: rgba(255, 255, 255, 0.08);
        border-color: rgba(255, 255, 255, 0.2);
        font-family: 'JetBrains Mono', monospace;
      }
      .tv-q-chip-cat {
        color: white;
        background: rgba(245, 158, 11, 0.18);
        border-color: rgba(245, 158, 11, 0.5);
      }
      .tv-q-chip-elim {
        margin-left: auto;
        color: #fca5a5;
        background: rgba(239, 68, 68, 0.14);
        border-color: rgba(239, 68, 68, 0.4);
      }
      .tv-q-prompt {
        font-family: 'Orbitron', 'Rajdhani', sans-serif;
        font-size: 42px;
        font-weight: 700;
        line-height: 1.2;
        text-align: center;
        color: white;
        text-shadow:
          0 4px 14px rgba(0, 0, 0, 0.6),
          0 0 30px rgba(245, 158, 11, 0.2);
        letter-spacing: 0.02em;
        margin-bottom: 22px;
      }
      .tv-bar-row {
        display: flex;
        align-items: center;
        gap: 16px;
      }
      .tv-bar-track {
        flex: 1;
        height: 10px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.06);
        overflow: hidden;
      }
      .tv-bar-fill {
        height: 100%;
        background: linear-gradient(90deg, #f59e0b, #fbbf24);
        box-shadow: 0 0 16px rgba(245, 158, 11, 0.7);
        transition: width 0.18s linear, background 0.3s;
        border-radius: 999px;
      }
      .tv-bar-fill.warn {
        background: linear-gradient(90deg, #ef4444, #f97316);
        box-shadow: 0 0 16px rgba(239, 68, 68, 0.7);
        animation: gx-timer-warn 0.6s ease-in-out infinite;
      }
      .tv-bar-num {
        min-width: 60px;
        text-align: right;
        font-family: 'JetBrains Mono', monospace;
        font-size: 24px;
        font-weight: 900;
        color: white;
        text-shadow: 0 0 12px rgba(245, 158, 11, 0.6);
      }
      .tv-bar-num.warn { color: #fca5a5; text-shadow: 0 0 12px rgba(239, 68, 68, 0.6); }

      .tv-choices {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 18px;
      }
      .tv-choice {
        position: relative;
        display: flex;
        align-items: center;
        gap: 22px;
        padding: 22px 26px;
        border-radius: 18px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        min-height: 96px;
        transition: opacity 0.35s, filter 0.35s, transform 0.35s, border-color 0.35s;
        overflow: hidden;
        animation: gx-slide-in-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
      }
      .tv-choice:nth-child(1) { animation-delay: 60ms; }
      .tv-choice:nth-child(2) { animation-delay: 120ms; }
      .tv-choice:nth-child(3) { animation-delay: 180ms; }
      .tv-choice:nth-child(4) { animation-delay: 240ms; }
      .tv-choice-letter {
        flex: 0 0 auto;
        width: 64px;
        height: 64px;
        border-radius: 14px;
        display: grid;
        place-items: center;
        font-family: 'Orbitron', sans-serif;
        font-size: 30px;
        font-weight: 900;
        color: #f59e0b;
        background: rgba(245, 158, 11, 0.12);
        border: 2px solid rgba(245, 158, 11, 0.55);
        text-shadow: 0 0 12px rgba(245, 158, 11, 0.55);
        flex-shrink: 0;
      }
      .tv-choice-text {
        font-size: 24px;
        font-weight: 600;
        line-height: 1.25;
        color: white;
        flex: 1;
        text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
      }
      .tv-choice-burst {
        position: absolute;
        top: 12px;
        right: 12px;
        width: 16px;
        height: 16px;
        color: rgba(245, 158, 11, 0.5);
      }
      .tv-choice-correct {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.32), rgba(34, 197, 94, 0.08));
        border-color: #22c55e;
        box-shadow:
          0 0 0 1px rgba(34, 197, 94, 0.6) inset,
          0 0 56px -8px rgba(34, 197, 94, 0.6);
        transform: scale(1.03);
      }
      .tv-choice-correct .tv-choice-letter {
        background: #22c55e;
        color: #0a1f0e;
        border-color: #22c55e;
        text-shadow: none;
        box-shadow: 0 0 18px rgba(34, 197, 94, 0.85);
      }
      .tv-choice-correct .tv-choice-burst { color: rgba(34, 197, 94, 0.85); }
      .tv-choice-sweep {
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%);
        animation: gx-glow-sweep 1.4s ease-out 1;
        pointer-events: none;
      }
      .tv-choice-dim {
        opacity: 0.32;
        filter: saturate(0.45);
      }

      .tv-reveal {
        display: flex;
        align-items: center;
        gap: 22px;
        padding: 18px 28px;
        border-radius: 18px;
        background: linear-gradient(90deg, rgba(34, 197, 94, 0.18), rgba(34, 197, 94, 0.04));
        border: 1px solid rgba(34, 197, 94, 0.55);
        box-shadow: 0 0 56px -16px rgba(34, 197, 94, 0.6);
        animation: gx-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both;
      }
      .tv-reveal-badge {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #22c55e;
        display: grid;
        place-items: center;
        box-shadow: 0 0 24px rgba(34, 197, 94, 0.7);
      }
      .tv-reveal-eyebrow {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 2.5px;
        text-transform: uppercase;
        color: #4ade80;
      }
      .tv-reveal-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 28px;
        font-weight: 800;
        color: white;
        text-shadow: 0 0 16px rgba(34, 197, 94, 0.55);
      }

      .tv-wrong-stamp {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 6;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        pointer-events: none;
        animation: tv-wrong-pop 0.35s cubic-bezier(0.16, 1, 0.3, 1) both,
                   tv-wrong-fade 0.3s ease-in 1.1s both;
      }
      .tv-wrong-x {
        font-size: 180px;
        line-height: 1;
        font-weight: 900;
        color: #ef4444;
        text-shadow:
          0 0 28px rgba(239, 68, 68, 0.95),
          0 0 70px rgba(239, 68, 68, 0.45),
          0 6px 18px rgba(0, 0, 0, 0.7);
        -webkit-text-stroke: 4px white;
      }
      .tv-wrong-label {
        text-align: center;
        background: rgba(0, 0, 0, 0.78);
        padding: 6px 18px;
        border-radius: 8px;
        border: 2px solid #ef4444;
        box-shadow: 0 0 24px rgba(239, 68, 68, 0.55);
      }
      .tv-wrong-name {
        font-family: 'Orbitron', sans-serif;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: 1.5px;
        color: white;
      }
      .tv-wrong-sub {
        font-family: 'Orbitron', sans-serif;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 3px;
        color: #fca5a5;
      }
      @keyframes tv-wrong-pop {
        0% { transform: translate(-50%, -50%) scale(0.4) rotate(-15deg); opacity: 0; }
        70% { transform: translate(-50%, -50%) scale(1.12) rotate(4deg); opacity: 1; }
        100% { transform: translate(-50%, -50%) scale(1) rotate(0deg); opacity: 1; }
      }
      @keyframes tv-wrong-fade {
        0% { opacity: 1; }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(1.15); }
      }

      .tv-timeup-stamp {
        position: absolute;
        inset: 0;
        z-index: 5;
        display: grid;
        place-items: center;
        pointer-events: none;
        background: radial-gradient(circle at 50% 50%, rgba(239, 68, 68, 0.25), transparent 70%);
        animation: tv-timeup-flash 1.6s ease-out forwards;
      }
      .tv-timeup-text {
        font-family: 'Orbitron', sans-serif;
        font-size: 110px;
        font-weight: 900;
        letter-spacing: 8px;
        color: white;
        text-shadow:
          0 0 40px #ef4444,
          0 0 80px rgba(239, 68, 68, 0.7),
          0 8px 24px rgba(0, 0, 0, 0.7);
        animation: tv-timeup-stamp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both,
                   tv-timeup-out 0.4s ease-in 1.2s both;
      }
      @keyframes tv-timeup-flash {
        0% { opacity: 0; }
        20% { opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes tv-timeup-stamp {
        0% { transform: scale(0.5); opacity: 0; letter-spacing: 24px; }
        100% { transform: scale(1); opacity: 1; letter-spacing: 8px; }
      }
      @keyframes tv-timeup-out {
        0% { transform: scale(1); opacity: 1; }
        100% { transform: scale(1.3); opacity: 0; }
      }

      .tv-end-card {
        padding: 56px 64px;
        border-radius: 28px;
        background: var(--gx-bg-strong);
        border: 1px solid rgba(245, 158, 11, 0.45);
        box-shadow: 0 0 100px -24px rgba(245, 158, 11, 0.55);
        text-align: center;
        min-width: 520px;
      }
      .tv-end-eyebrow {
        font-family: 'Orbitron', sans-serif;
        font-size: 14px;
        font-weight: 700;
        letter-spacing: 4px;
        text-transform: uppercase;
        color: #f59e0b;
      }
      .tv-end-title {
        margin-top: 12px;
        font-family: 'Orbitron', sans-serif;
        font-size: 56px;
        font-weight: 900;
        letter-spacing: 0.02em;
        color: white;
        text-shadow: 0 0 32px rgba(245, 158, 11, 0.6);
      }
      .tv-end-board {
        margin-top: 28px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .tv-end-row {
        display: flex;
        align-items: center;
        gap: 18px;
        padding: 12px 20px;
        border-radius: 12px;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(255, 255, 255, 0.07);
        font-size: 18px;
        font-weight: 600;
      }
      .tv-end-row.top {
        background: linear-gradient(90deg, rgba(245, 158, 11, 0.25), rgba(255, 255, 255, 0.03));
        border-color: #f59e0b;
        box-shadow: 0 0 32px -10px rgba(245, 158, 11, 0.6);
      }
      .tv-end-rank {
        font-family: 'JetBrains Mono', monospace;
        color: #f59e0b;
        min-width: 40px;
        font-size: 20px;
        font-weight: 900;
      }
      .tv-end-name { flex: 1; color: white; text-align: left; }
      .tv-end-score {
        font-family: 'JetBrains Mono', monospace;
        color: white;
        font-size: 22px;
        font-weight: 900;
      }
    `}</style>
  );
}
